import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useLoadingEntryImportSheet } from '@/feature/alerts/components/LoadingEntryImportSheetProvider';
import { useAlerts } from '@/feature/alerts/hooks/use-alerts';
import { useProductTransitionStore } from '@/feature/product-transition/use-product-transition-store';
import { useAvailableProducts } from '@/hooks/use-available-products';
import { Product, useJurisdictionStore } from '@/hooks/use-jurisdiction';
import { logger } from '@/utils/logging';

import { SocialLink, extractShortIdFromURL, importSocialLink, isSocialShareLink } from '../utils/social-link';
import {
    CrossProductImport,
    discardCrossProductImport,
    useLinking,
    useLinkingStore,
    usePendingSocialLink,
} from './use-linking';

const LOG_TAG = '[ShareLinkImport]';

// one source for every product's importer: consuming a link in one must consume it
// everywhere, else getInitialURL replays it into whichever importer mounts later
const SHARE_LINK_IMPORT_SOURCE = 'share-link-import';

type JurisdictionState = ReturnType<typeof useJurisdictionStore.getState>;

const isShareImportEnabledFor = (state: JurisdictionState, product: Product) =>
    !!state.jurisdictionSettings?.products?.find(it => it.uid.includes(product))?.settings
        .enable_share_and_reuse_lineups;

const SHARE_IMPORTING_PRODUCTS = [Product.Pickem, Product.Sweeps, Product.Prediction];

export const socialLinkDestination = (socialLink: SocialLink): Product => {
    switch (socialLink.type) {
        case 'sbk':
            return Product.Sweeps;
        case 'predictions':
            return Product.Prediction;
        case 'fantasy':
            return Product.Pickem;
    }
};

export const useLineupUnavailableSheet = () => {
    const { showInfoSheet } = useAlerts();
    const { t } = useTranslation('lineup_update_modal');
    return useCallback(() => {
        showInfoSheet({
            title: t('lineup_unavailable_title'),
            description: t('lineup_unavailable_info'),
            buttonLabel: t('browse_players'),
        });
    }, [showInfoSheet, t]);
};

export const useShareImportSheet = () => {
    const { t } = useTranslation('common');
    const { showImportingEntrySheet, dismissImportingEntrySheet } = useLoadingEntryImportSheet();

    const showShareImportSheet = useCallback(() => {
        showImportingEntrySheet({
            title: t('importing_shared_picks_title'),
            description: t('importing_shared_picks_description'),
        });
    }, [showImportingEntrySheet, t]);

    return { showShareImportSheet, dismissShareImportSheet: dismissImportingEntrySheet };
};

export const useBetUnavailableSheet = () => {
    const { showInfoSheet } = useAlerts();
    const { t } = useTranslation(['betslip_sbk', 'common']);
    return useCallback(() => {
        showInfoSheet({
            buttonLabel: t('common:got_it'),
            title: t('bet_unavailable_title'),
            description: t('bet_unavailable_info'),
        });
    }, [showInfoSheet, t]);
};

const useUnavailableSheetFor = () => {
    const showLineupUnavailable = useLineupUnavailableSheet();
    const showBetUnavailable = useBetUnavailableSheet();

    return useCallback(
        (product: Product) => (product === Product.Pickem ? showLineupUnavailable() : showBetUnavailable()),
        [showBetUnavailable, showLineupUnavailable]
    );
};

// for destinations with their own entry gate: once the gate lands somewhere the import can never
// run, the parked hand-off is dropped and the user told, else it replays if the gate later clears
export const useDiscardPendingShareImport = (product: Product, discard: boolean) => {
    const showUnavailable = useUnavailableSheetFor();
    useEffect(() => {
        if (discard && discardCrossProductImport(product)) {
            showUnavailable(product);
        }
    }, [discard, product, showUnavailable]);
};

export const useHandOffShareImport = () => {
    const { isProductAvailable, isProductHiddenByUser } = useAvailableProducts();
    const showUnavailable = useUnavailableSheetFor();

    return useCallback(
        (destination: Product, shortId: string, socialLink: SocialLink) => {
            if (isProductHiddenByUser(destination)) {
                // app-root HiddenProductRedirectListener turns this into the standard sheet
                useLinkingStore.getState().setUnavailableProductRedirect(destination);
                return;
            }
            if (
                !isProductAvailable(destination) ||
                !isShareImportEnabledFor(useJurisdictionStore.getState(), destination)
            ) {
                showUnavailable(destination);
                return;
            }
            useLinkingStore.getState().setCrossProductImport({ shortId, socialLink, product: destination });
            useProductTransitionStore.getState().actions.triggerTransition(destination);
        },
        [isProductAvailable, isProductHiddenByUser, showUnavailable]
    );
};

export const useHandleSocialLink = (
    product: Product,
    onImport?: (shortId: string, prefetched?: SocialLink) => void | Promise<void>
) => {
    const { t } = useTranslation('common');
    const { showToast } = useAlerts();
    const { showShareImportSheet, dismissShareImportSheet } = useShareImportSheet();
    const showUnavailable = useUnavailableSheetFor();
    const handOffToProduct = useHandOffShareImport();

    // any importing product's flag is enough; dispatch re-checks the destination's own
    const enabled = useJurisdictionStore(state =>
        SHARE_IMPORTING_PRODUCTS.some(destination => isShareImportEnabledFor(state, destination))
    );

    const dispatch = useCallback(
        async (shortId: string, socialLink: SocialLink) => {
            const destination = socialLinkDestination(socialLink);
            if (destination !== product || !onImport) {
                handOffToProduct(destination, shortId, socialLink);
                return;
            }
            // the hand-off pre-checks this, but config can change mid-transition
            if (!isShareImportEnabledFor(useJurisdictionStore.getState(), product)) {
                showUnavailable(product);
                return;
            }
            await onImport(shortId, socialLink);
        },
        [handOffToProduct, onImport, product, showUnavailable]
    );

    const handleUrl = useCallback(
        async (url: string) => {
            if (!isSocialShareLink(url) || !enabled) {
                return;
            }
            logger.info(LOG_TAG, `Open link ${url}`);
            const shortId = extractShortIdFromURL(url);
            if (!shortId) {
                showToast({ message: t('invalid_link') });
                return;
            }
            try {
                showShareImportSheet();
                const socialLink = await importSocialLink(shortId);
                logger.debug(LOG_TAG, 'resolved social link', socialLink);
                if (socialLink) {
                    await dispatch(shortId, socialLink);
                } else {
                    showToast({ message: t('invalid_link') });
                }
            } catch (error) {
                showToast({ message: t('invalid_link') });
                logger.warn(LOG_TAG, error);
            } finally {
                dismissShareImportSheet();
            }
        },
        [dismissShareImportSheet, dispatch, enabled, showShareImportSheet, showToast, t]
    );

    useLinking({
        source: SHARE_LINK_IMPORT_SOURCE,
        product,
        onInitialLink: handleUrl,
        onNewLink: handleUrl,
    });

    const importFromHandoff = useCallback(
        async ({ shortId, socialLink }: CrossProductImport) => {
            try {
                showShareImportSheet();
                await dispatch(shortId, socialLink);
            } finally {
                dismissShareImportSheet();
            }
        },
        [dismissShareImportSheet, dispatch, showShareImportSheet]
    );
    usePendingSocialLink(product, importFromHandoff);
};
